import { createSlice } from "@reduxjs/toolkit";
import {
  createPatient,
  getPatientDetails,
  getPatientShippingAddresses,
  postPatientShippingAddress,
  patchPatientShippingAddress,
  patchPatient,
} from "./patientActions";
import LocalStorageService from "../../common/services/LocalStorageService";

const initialState = {
  loading: false,
  error: null,
  success: false,
  unAuthenticatedPatientId: LocalStorageService.get("patientId") || "",
  // The full name the guest typed at checkout, sent on the order as
  // recipient_name: a phone already on file keeps that customer's own name.
  unAuthenticatedRecipientName: LocalStorageService.get("recipientName") || "",
  // The signed proof that this browser created that guest record, sent back
  // on every guest request for it (see guestToken.js). Empty for a guest id
  // saved before tokens existed: the backend answers 404 without one, and
  // checkout then forgets the id and asks for the details again.
  unAuthenticatedGuestToken: LocalStorageService.get("guestToken") || "",
  patient: {},
  shipping_addresses: {},
};

const patientSlice = createSlice({
  name: "patient",
  initialState,
  reducers: {
    resetPatientError: (state, action) => {
      if (action?.payload) {
        delete state.error[action?.payload];
        if (Object.keys(state.error)?.length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
    addPatient: (state, action) => {
      state.unAuthenticatedPatientId = action?.payload?.id;
      LocalStorageService.set("patientId", action?.payload?.id);
      state.unAuthenticatedRecipientName = action?.payload?.recipient_name || "";
      if (state.unAuthenticatedRecipientName) {
        LocalStorageService.set(
          "recipientName",
          state.unAuthenticatedRecipientName
        );
      } else {
        LocalStorageService.remove("recipientName");
      }
      // Always replaced along with the id: a token only opens its own record.
      state.unAuthenticatedGuestToken =
        typeof action?.payload?.guest_token === "string"
          ? action.payload.guest_token
          : "";
      if (state.unAuthenticatedGuestToken) {
        LocalStorageService.set("guestToken", state.unAuthenticatedGuestToken);
      } else {
        LocalStorageService.remove("guestToken");
      }
    },
    // Drops the guest record this browser holds (payload: its id), so checkout
    // asks for the details again. Ignored once another id replaced it.
    forgetGuestPatient: (state, action) => {
      if (
        !state.unAuthenticatedPatientId ||
        String(state.unAuthenticatedPatientId) !== String(action?.payload)
      ) {
        return;
      }
      state.unAuthenticatedPatientId = "";
      state.unAuthenticatedRecipientName = "";
      state.unAuthenticatedGuestToken = "";
      state.patient = {};
      state.shipping_addresses = {};
      state.error = null;
      state.loading = false;
      LocalStorageService.remove("patientId");
      LocalStorageService.remove("recipientName");
      LocalStorageService.remove("guestToken");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPatient.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPatient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getPatientDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPatientDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.patient = action.payload;
      })
      .addCase(getPatientDetails.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(patchPatient.pending, (state) => {
        state.loading = true;
      })
      .addCase(patchPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patient = action.payload;
        state.success = true;
      })
      .addCase(patchPatient.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getPatientShippingAddresses.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPatientShippingAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.shipping_addresses = action.payload;
      })
      .addCase(getPatientShippingAddresses.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(postPatientShippingAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(postPatientShippingAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.shipping_addresses = {
          meta: state?.shipping_addresses?.meta,
          results: [action?.payload, ...state?.shipping_addresses?.results],
        };
      })
      .addCase(postPatientShippingAddress.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(patchPatientShippingAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(patchPatientShippingAddress.fulfilled, (state, action) => {
        const updatedAddresses = state?.shipping_addresses?.results?.map(
          (el) => {
            if (el?.id === action?.payload?.id) {
              return action.payload;
            } else {
              return el;
            }
          }
        );
        const newAddresses = {
          meta: state?.shipping_addresses?.meta,
          results: updatedAddresses,
        };
        state.shipping_addresses = newAddresses;
        state.loading = false;
      })
      .addCase(patchPatientShippingAddress.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});
export const { addPatient, resetPatientError, forgetGuestPatient } =
  patientSlice.actions;
export default patientSlice.reducer;
